import { createRef, RefObject } from "react";
import moment from "moment";

import { AddContactPointWarningPopup } from "@/Appointments/Components/Calendar/AddContactPointWarningPopup";
import { AddRepresentativeEmailPopup, AddRepresentativeEmailPopupProps } from "@/Appointments/Components/Calendar/AddRepresentativeEmailPopup";
import { IAppointmentEditModel } from "@/Appointments/Models/UI/IAppointmentEditModel";
import { Client } from "@/Common/Api/Client";
import { ImportedPatientMappingResponse } from "@/Common/Api/ExternalCommunications/Dto/ImportedPatientMappingResponse";
import { IRepresentantsDto } from "@/Common/Api/Patient/Dto/IRepresentantsDto";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { PatientImportComponent } from "@/Common/Components/Import/PatientImportComponent";
import { PatientImportValidationResult } from "@/Common/Components/Import/PatientImportValidationResult";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TimeSelector } from "@/Common/Components/Inputs/TimeSelector";
import { PatientBannerVertical } from "@/Common/Components/Patient/PatientBannerVertical";
import { PatientEdit, PatientEditState } from "@/Common/Components/Patient/PatientEdit";
import { PortalPatientSync } from "@/Common/Components/Portal/PortalSync/PortalPatientSync";
import { QuestionnairePopupController } from "@/Common/Components/Questionnaire/QuestionnairePopupController";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { ReferalDoctorSearchAutocompleteCustom } from "@/Common/Components/Search/ReferalDoctorSearchAutocompleteCustom";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { PatientEmailValidator } from "@/Common/Helpers/PatientEmailValidator";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { IImportablePatient } from "@/Common/Models/Patient/IPatient";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { IJPatientCreationModel, IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { Person } from "@/Common/Models/Person/Person";
import { QuestionnaireName } from "@/Common/Models/Questionnaires/QuestionnaireName";
import { QuestionnaireStatusInfo } from "@/Common/Models/Questionnaires/QuestionnaireStatusInfo";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { ServiceModel } from "@/Common/Models/Services/ServiceModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { DirectoryProfessionalSearchLockingComponent } from "@/ContactDirectory/Components/DirectoryProfessionalSearchLockingComponent";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models/DirectoryHealthCareProvider";
import { ThirdPartyPatientImportHandler } from "../ThirdPartyPatientImportHandler";

import styles from "./AppointmentEditDialog.module.scss";

import img_loading from "@/Globals/_images/loading.gif";

interface IEditAppointmentDialogProps {
    AppointmentEditModel: IAppointmentEditModel;
    PatientCreationModel: IPatientCreationModel;
    PatientPortalModel: Person;
    PatientBannerModel: IPatientBannerModel;
    AppointmentTimePickerInterval?: number;
    ActivePatientContactId?: string;
    IsSaving?: boolean;
}

class EditAppointmentDialogState {
    public IsSaving: boolean;
    public PatientCreateEditState: IPatientCreationModel;
    public PatientCreateFormIsValid: boolean;
    public isPatientSaving: boolean;

    public showAddEmailPopup: boolean = false;
    public addEmailPopupProps: AddRepresentativeEmailPopupProps;
    public representantsDto: IRepresentantsDto;

    public showAddContactPointWarning: boolean = false;
    public isSaveAndOpenAction: boolean = false;

    public hasEmailNotificationsEnabled: boolean = false;
    public hasSmsNotificationsEnabled: boolean = false;

    public importedPatients: ImportedPatientMappingResponse[] = [];

    constructor(s: EditAppointmentDialogState = null) {
        if (s != null) {
            this.IsSaving = s.IsSaving;
            this.PatientCreateEditState = s.PatientCreateEditState;
            this.PatientCreateFormIsValid = s.PatientCreateFormIsValid;
            this.isPatientSaving = s.isPatientSaving;
        }
    }
}

class EditAppointmentDialog extends BaseReactComponent<IEditAppointmentDialogProps, EditAppointmentDialogState> {
    private containerRef: RefObject<HTMLDivElement>;
    private clinicianSearchStrategy: ClinicianSearchStrategy;
    private externalPatientImportValidationRules: ((importedPatientInfo: IImportablePatient, selectedPatient: Person) => PatientImportValidationResult)[];

    constructor(props: IEditAppointmentDialogProps) {
        super(props);
        this.containerRef = createRef<HTMLDivElement>();

        this.initData();

        const initialState = new EditAppointmentDialogState();
        initialState.IsSaving = false;
        initialState.isPatientSaving = false;
        this.state = initialState;
    }

    public componentDidMount(): void {
        const elem = $(this.containerRef.current);
        const cnt = document.body;
        const dragOptions = {
            containment: cnt,
            handle: `.${styles.header}`,
            stop: function() {
                elem.draggable("destroy");
                elem.css({ width: "auto" });
                window.setTimeout(() => elem.draggable(dragOptions), 80);
            }
        };

        elem.draggable(dragOptions);

        this.blockFieldsForPortalAppointment();

        if (this.props.AppointmentEditModel.get("SelectedPatientId"))
            calendarStore.calendarActions.LoadPatient(calendarStore.appointmentEditModel);
    }

    public componentDidUpdate(prevProps: Readonly<IEditAppointmentDialogProps>): void {
        if (this.props !== prevProps) {
            this.initData();

            const selectedSite = cacheStore.GetSiteById(this.props.AppointmentEditModel.get("SelectedSiteId"));

            const hubNotificationsEnabled = selectedSite.Model.BookingHubEnabled && this.props.AppointmentEditModel.get("SelectedIntervenors").some(p => p.EmployeeModel.BookingHubEnabled);

            const hasEmailNotificationsEnabled = hubNotificationsEnabled || (cacheStore.ClinicSettings_Portal.EnablePortalNotifications && cacheStore.portalConfiguration?.HasEmailNotificationEnabled);
            const hasSmsNotificationsEnabled = hubNotificationsEnabled || (cacheStore.ClinicSettings_Portal.EnablePortalNotifications && cacheStore.portalConfiguration?.HasSmsNotificationEnabled);

            this.mutateState(state => {
                state.hasEmailNotificationsEnabled = hasEmailNotificationsEnabled;
                state.hasSmsNotificationsEnabled = hasSmsNotificationsEnabled;
            });
        }

        this.blockFieldsForPortalAppointment();
    }

    public shouldComponentUpdate(nextProp: IEditAppointmentDialogProps, nextState: EditAppointmentDialogState): boolean {
        return (
            this.props.AppointmentEditModel !== nextProp.AppointmentEditModel
            || this.props.PatientBannerModel !== nextProp.PatientBannerModel
            || this.props.PatientCreationModel !== nextProp.PatientCreationModel
            || this.props.PatientPortalModel !== nextProp.PatientPortalModel
            || this.state != nextState
        );
    }

    public render(): JSX.Element {
        const underlyingAppointmentModel = this.props.AppointmentEditModel.get("Appointment");
        const isPortalAppointment = underlyingAppointmentModel.IsSharedWithPortal && underlyingAppointmentModel.ExternalId != null;
        const isExternalAppointment = underlyingAppointmentModel.IsExternallyBookedAppointment; // external appointment here means that the appt was not booked from within medfar's system but through a 3rd party (e.g. appointment hub)

        const selectedPatientId = this.props.AppointmentEditModel && this.props.AppointmentEditModel.get("SelectedPatientId");
        let shouldDisplayExistingPatientPanel = false;
        let shouldDisplayPatientCreation = false;
        let shouldDisplayPatientPortalLink = false;
        let shouldDisplayExternalPatientLink = false;
        let hasExistingPatient = false;
        const isAppointmentEditDisabled = cacheStore.ClinicSettings.IsCalendarReadOnly || underlyingAppointmentModel.IsReadOnly;

        if (selectedPatientId) {
            shouldDisplayExistingPatientPanel = true;
            hasExistingPatient = true;
        }
        else if (this.props.AppointmentEditModel.get("IsNewPatientCreationMode")) {
            shouldDisplayPatientCreation = true;
        }
        else if (this.props.AppointmentEditModel.get("IsLinkPatientPortalMode")) {
            shouldDisplayPatientPortalLink = true;
        }
        else if (this.props.AppointmentEditModel.get("IsLinkThirdPartyPatientMode")) {
            shouldDisplayExternalPatientLink = true;
        }

        if (shouldDisplayExternalPatientLink)
            return this.renderLinkThirdPartyPatientForm();

        if (shouldDisplayPatientPortalLink)
            return this.renderLinkPatientPortalForm();

        return (
            <div>
                <div
                    ref={this.containerRef}
                    onClick={e => this.stopPropagation(e)}
                    className="popup cal myle-form sm"
                    style={{ marginLeft: "-12%", overflow: "visible", position: "absolute" }}
                >
                    {shouldDisplayPatientCreation
                        ? this.renderHeaderCreatePatient()
                        : this.renderHeaderEditAppointment(isPortalAppointment, isAppointmentEditDisabled)
                    }

                    {this.state.showAddEmailPopup &&
                        <AddRepresentativeEmailPopup {...this.state.addEmailPopupProps} />
                    }

                    <div className="p-body">
                        <div className="tb">
                            <div className="tbc left">
                                {shouldDisplayPatientCreation
                                    ? this.renderCreatePatientForm()
                                    : this.renderEditAppointmentForm(isPortalAppointment, hasExistingPatient, isExternalAppointment, isAppointmentEditDisabled)
                                }
                            </div>

                            <div className="tbc right right-left">
                                {shouldDisplayExistingPatientPanel &&
                                    this.renderPatientInformationPanel()
                                }
                            </div>
                        </div>
                    </div>

                    {shouldDisplayPatientCreation
                        ? this.renderFooterCreatePatient()
                        : this.renderFooterEditAppointment(isPortalAppointment, isAppointmentEditDisabled)
                    }
                </div>

                {this.props.AppointmentEditModel.get("ShowQuestionnaire") && (
                    <QuestionnairePopupController
                        questionnaire={this.props.AppointmentEditModel.get("Questionnaire")}
                        onClose={() => sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Questionnaire_OnClick, { showQuestionnaire: false })}
                        onSubmitted={result => sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Questionnaire_OnSubmitted, { result })}
                    />
                )}

                {this.state.showAddContactPointWarning && (
                    <AddContactPointWarningPopup
                        patient={this.props.AppointmentEditModel.get("SelectedPatient").first()}
                        onClose={() => this.mutateState(s => {
                            s.showAddContactPointWarning = false;
                            s.IsSaving = false;
                        })}
                        onContinue={(emailContactPointId, phoneContactPointId) => {
                            this.addCreatedContactPointIdsToStore([emailContactPointId, phoneContactPointId].filter(p => !!p));
                            this.mutateState(
                                s => s.showAddContactPointWarning = false,
                                () => this.state.isSaveAndOpenAction ? this.saveAndOpen() : this.save()
                            );
                        }}
                        onContinueWithoutContact={() => this.mutateState(s => s.showAddContactPointWarning = false, () => this.state.isSaveAndOpenAction ? this.saveAndOpen() : this.save())}
                    />
                )}
            </div>
        );
    }

    private renderHeaderEditAppointment(isPortalAppointment: boolean, isAppointmentEditDisabled: boolean): JSX.Element {
        const isEvent = this.props.AppointmentEditModel.get("IsCalendarEventTab");

        const headerText = cacheStore.ClinicSettings.EnableNewPatientlessAppointmentBehavior
            ? cacheStore.GetString("AppointmentDetails_BookingLabel")
            : cacheStore.GetString("AppointmentDetailsLabel");

        return (
            <div className={styles.header}>
                <h4 className={styles.title}>{headerText}</h4>
                <div className={styles.links}>
                    {!isAppointmentEditDisabled && (
                        <div className="inline-block" data-cy="appointmentEditDialog-links-div">
                            <a onClick={() => this.onCopyClick()}>{cacheStore.GetString("CopyLabel")}</a>
                            <span className="separator">|</span>
                            <a onClick={() => this.onCutClick()}>{cacheStore.GetString("CutLabel")}</a>
                            <span className="separator">|</span>
                            <a onClick={() => this.onPasteClick()}>{cacheStore.GetString("PasteLabel")}</a>
                            {!isEvent && <span className="separator">|</span>}
                        </div>
                    )}
                    {!isEvent && (
                        <>
                            <a onClick={() => this.onPrintClick()}>{cacheStore.GetString("PrintLabelLabel")}</a>
                            {this.shouldShowPrintCertificate(isPortalAppointment) && (
                                <div style={{ display: "inline-block" }}>
                                    <span className="separator">|</span>
                                    <a onClick={() => this.onPrintCertificate()}>{cacheStore.GetString("PrintCertificateLabel")}</a>
                                </div>
                            )}
                        </>
                    )}
                    <a className={styles.close} onClick={() => this.onCancelClick()}>×</a>
                </div>
            </div>
        );
    }

    private renderFooterEditAppointment(isPortalAppointment: boolean, isAppointmentEditDisabled: boolean): JSX.Element {
        const isTimeInvalid = this.props.AppointmentEditModel.get("EndTime") <= this.props.AppointmentEditModel.get("StartTime");
        const isEvent = this.props.AppointmentEditModel.get("IsCalendarEventTab");
        const flexEndClass = isEvent ? "flex-end" : "";

        return (
            <div className={styles.footer}>
                <div className={`flex-container ${flexEndClass}`}>
                    {!isEvent && (
                        <div style={{ alignSelf: "center" }}>
                            {!isAppointmentEditDisabled && (
                                <CheckboxControl
                                    id="printLabelOnSave"
                                    labelStyle={{ display: "inline" }}
                                    checked={this.props.AppointmentEditModel.get("IsPrintLabelOnSave")}
                                    label={cacheStore.GetString(cacheStore.ClinicSettings.EnableNewPatientlessAppointmentBehavior ? "AppointmentWithNoPatient_PrintLabelOnSaveLabel" : "PrintLabelOnSaveLabel")}
                                    operation={LocalOperations.Calendar.Checkbox_Change}
                                    target={CheckboxTarget.Myle_Calendar_Appointment_IsPrintLabelOnSave}
                                />
                            )}
                            {(this.shouldShowPrintCertificate(isPortalAppointment) && !isAppointmentEditDisabled) && (
                                <CheckboxControl
                                    id="printCertificateOnSave"
                                    labelStyle={{ display: "inline" }}
                                    checked={this.props.AppointmentEditModel.get("IsPrintCertificateOnSave")}
                                    label={cacheStore.GetString("PrintCertificateOnSaveLabel")}
                                    operation={LocalOperations.Calendar.Checkbox_Change}
                                    target={CheckboxTarget.Myle_Calendar_Appointment_IsPrintCertificateOnSave}
                                />
                            )}
                        </div>
                    )}

                    <ButtonGroup>
                        <ButtonControl
                            variant="neutral"
                            onClick={() => this.onCancelClick()}
                            label={cacheStore.GetString("CancelLabel")}
                            dataCy="appointmentEditDialog-cancel-button"
                        />

                        {isAppointmentEditDisabled && this.props.AppointmentEditModel.get("SelectedPatient")
                            ? (
                                <ButtonControl
                                    variant="brand"
                                    onClick={() => sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_OnOpen)}
                                    label={cacheStore.GetString("OpenLabel")}
                                />
                            )
                            : (
                                <>
                                    <ButtonControl
                                        variant="brand"
                                        disabled={isTimeInvalid || this.state.IsSaving}
                                        onClick={() => this.onSaveClick()}
                                        label={cacheStore.GetString("SaveLabel")}
                                        dataCy="appointmentEditDialog-save-button"
                                    />

                                    {!isEvent && (
                                        <ButtonControl
                                            variant="brand"
                                            disabled={isTimeInvalid || this.state.IsSaving}
                                            onClick={() => this.onSaveAndOpenClick()}
                                            label={cacheStore.GetString("SaveAndOpenLabel")}
                                            dataCy="appointmentEditDialog-saveAndOpen-button"
                                        />
                                    )}
                                </>
                            )
                        }
                    </ButtonGroup>
                </div>
            </div>
        );
    }

    private renderEditAppointmentForm(isPortalAppointment: boolean, hasExistingPatient: boolean, isExternalAppointment: boolean, isAppointmentEditDisabled: boolean): JSX.Element {
        const enablePatientLessAppointment = cacheStore.ClinicSettings.EnableNewPatientlessAppointmentBehavior;
        const appEditModel = this.props.AppointmentEditModel;
        const underlyingAppointmentModel = appEditModel.get("Appointment");
        const selectedPatients = appEditModel.get("SelectedPatient");
        const patientSearchString = appEditModel.get("PatientSearchString");
        const shouldShowLinkExternalPatient = !hasExistingPatient && isExternalAppointment;
        const appEditModelHasPatient = patientSearchString?.trim() || selectedPatients.size > 0;
        const isCombo = this.isCombo();
        const isEvent = appEditModel.get("IsCalendarEventTab") && enablePatientLessAppointment;
        const isCreateMode = !appEditModel.get("Appointment")?.AppointmentId || appEditModel.get("IsNewAppointment");
        const hasTabs = (isCreateMode || !appEditModel.get("Appointment")?.HasPatient) && enablePatientLessAppointment;
        const hasNotificationCheckbox =
            (!isEvent || appEditModel.get("NotifyPatient"))
            && (this.state.hasEmailNotificationsEnabled || this.state.hasSmsNotificationsEnabled);
        const hasTeleconsultationCheckbox =
            cacheStore.ClinicSettings.EnableIsTeleconsultationCheckbox
            && (!isEvent || appEditModel.get("IsTeleconsultation"));
        let eventTabClasses = isEvent ? styles.activeItem : "";

        if (appEditModelHasPatient)
            eventTabClasses += " " + styles.disabledItem;

        const disablePatientEdit = isCombo || isAppointmentEditDisabled || underlyingAppointmentModel.IsSynchronized;

        // get a list of service definitions available for site/employe as "default appointment subjects"
        const subjectAutocompleteServiceModels = appEditModel.get("DefaultSubjects")?.map(x => new SearchableItemModelT<ServiceModel>(x.GetNameWithDuration(), x.ServiceID, x)).toImmutable();
        const availableQuestionnaires = cacheStore?.AvailableQuestionnaires.map(x => new SearchableItemModelT<QuestionnaireName>(x.ShortName, x.QuestionnaireId, x)).toImmutable();

        // get site list associated to appointment intervenor(s)
        const appointmentIntervenor = appEditModel.get("AppointmentIntervenor");
        const appointmentIntervenorSites = appointmentIntervenor?.EmployeeModel
            ? cacheStore.SiteCache.filter(site =>
                site.Model.Id === underlyingAppointmentModel.SiteId
                || appointmentIntervenor.EmployeeModel.AssociatedSites.includes(site.Model.Id)
            )
            : cacheStore.SiteCache;

        const addPatientLinkRow = (
            <div>
                <div className="flex-container" style={{ justifyContent: "flex-start", marginBottom: "5px" }}>
                    <div className="vcenter text-right" style={{ width: "165px", paddingRight: "30px" }} />
                    {(patientStore.CanCreatePatient && !isAppointmentEditDisabled) && (
                        <div className="" style={{ width: "74%" }} data-cy="appointmentEditDialog-createPatient-link">
                            <a className="right" onClick={() => this.onCreatePatientClick()}> {cacheStore.GetString("CreatePatientFileLabel")}</a>
                        </div>
                    )}
                </div>
            </div>
        );

        const linkPatientPortalRow = (
            <div>
                <div className="flex-container" style={{ justifyContent: "flex-start", marginBottom: "5px" }}>
                    <div className="vcenter text-right" style={{ width: "165px", paddingRight: "30px" }} />
                    {patientStore.CanEditPatient && (
                        <div className="" style={{ width: "74%" }}>
                            <a className="right" onClick={() => this.onLinkPatientPortalClick()}> {cacheStore.GetString("PortalPatientSearchAttachFileLabel")}</a>
                        </div>
                    )}
                </div>
            </div>
        );

        const linkExternalPatientRow = (
            <div>
                <div className="flex-container" style={{ justifyContent: "flex-start", marginBottom: "5px" }}>
                    <div className="vcenter text-right" style={{ width: "165px", paddingRight: "30px" }} />
                    <div className="" style={{ width: "74%" }}>
                        <a className="right" onClick={() => this.onLinkExternalPatientClick()}> {cacheStore.GetString("PortalPatientSearchAttachFileLabel")} (HUB)</a>
                    </div>
                </div>
            </div>
        );

        return (
            <div className="scroll-limiter-main">
                <div className="row">
                    <div className="col-sm-11 col-md-11 col-lg-11">
                        {hasTabs && (
                            <div className="row">
                                <div className="col-sm-12">
                                    <div className={styles.switcherContainer}>
                                        <a
                                            className={!isEvent ? styles.activeItem : ""}
                                            onClick={() => sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Tab_OnChange, { isCalendarEventTab: false })}
                                        >
                                            {cacheStore.GetString("AppointmentLabel")}
                                        </a>
                                        <a
                                            data-cy="create-patientless-appointment"
                                            className={eventTabClasses}
                                            onClick={() => this.onEventTabClick()}
                                        >
                                            {cacheStore.GetString("EventLabel")}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}

                        {// display a warning explaining that a PORTAL appointment cannot have their patient / intervenors / service / subject / questionnaire changed
                            isCombo && (
                                <div className="portal-appointment-edit-warning">
                                    <span>
                                        <i> {cacheStore.ClinicSettings.EnableCovidQuestionnaire
                                            ? cacheStore.GetString("ComboAppointmentQuestionnaireEditWarning")
                                            : cacheStore.GetString("ComboAppointmentEditWarning")
                                        }
                                        </i>
                                    </span>
                                </div>
                            )
                        }

                        {!isEvent && (
                            <div className="row">
                                <div className="col-md-3 col-lg-3 vcenter text-right">
                                    <label className="form-label">{cacheStore.GetString("PatientsLabel")}</label>
                                </div>
                                <div className="col-sm-9 col-md-9 col-lg-9">
                                    <PersonSearchAutocompleteCustom
                                        disabled={disablePatientEdit}
                                        useBlurEvent={true}
                                        focusOnLoad={selectedPatients.size === 0}
                                        componentId="add_appointment_patients"
                                        dataCy="appointmentEditDialog-patients"
                                        selectedItemList={selectedPatients}
                                        selectionOp={LocalOperations.Calendar.AppointmentEdit_Patient_OnChange}
                                        clearOp={LocalOperations.Calendar.AppointmentEdit_Patient_OnClear}
                                        closeDropDownAfterSelection={true}
                                        changedTextValueOp={LocalOperations.Calendar.AppointmentEdit_Patient_TextChange}
                                        defaultTextValue={patientSearchString}
                                    />
                                </div>
                            </div>
                        )}

                        {// allow new patient creation if appointment has no existing linked patient AND ID  current user has adequate privileges
                            !hasExistingPatient &&
                            patientStore.CanCreatePatient &&
                            !isPortalAppointment &&
                            !isExternalAppointment &&
                            !isEvent &&
                            addPatientLinkRow
                        }

                        {!hasExistingPatient &&
                            patientStore.CanCreatePatient &&
                            isPortalAppointment &&
                            !isEvent &&
                            linkPatientPortalRow
                        }

                        {shouldShowLinkExternalPatient && !isEvent &&
                            linkExternalPatientRow
                        }

                        <div className="row">
                            <div className="col-md-3 col-lg-3 vcenter text-right">
                                <label className="form-label">{cacheStore.GetString("SubjectLabel")}</label>
                            </div>
                            <div className="col-sm-9 col-md-9 col-lg-9">
                                <TextboxControl
                                    value={appEditModel.get("Subject")}
                                    autoCompleteItems={isCombo ? [].toImmutable() : subjectAutocompleteServiceModels}
                                    onAutoCompleteSelectClearTextBox={true}
                                    onAutoCompleteSelectCallBack={(code, model: ServiceModel) => sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Subject_onServiceSelected, { serviceModel: model })}
                                    onAutoCompleteSelectDoNotUpdateTextBox={false}
                                    onChange={(customSubject: string) => sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Subject_OnChange, { customSubject })}
                                    style={{ marginRight: "10px" }}
                                    placeholder=""
                                    id="appointment-edit-subject"
                                    dataCy="appointmentEditDialog-subject"
                                    disabled={isAppointmentEditDisabled}
                                />
                            </div>
                        </div>

                        {appEditModel.get("SelectedSubjectService") != null && (
                            <div className="flex-container" style={{ justifyContent: "flex-start", marginBottom: "5px" }}>
                                <div className="vcenter text-right" style={{ width: "165px", paddingRight: "30px" }} />
                                <div id="selectedService" data-cy="appointmentEditDialog-selectedService-div" style={{ fontSize: "16px", width: "74%" }}>
                                    <span className="bold"> {cacheStore.GetString("ServiceLabel")}: </span>
                                    <span style={{ fontStyle: "italic" }}><i>{appEditModel.get("SelectedSubjectService").GetNameWithDuration()}</i></span>
                                    {!isAppointmentEditDisabled && (
                                        <div className="pointer" style={{ color: "red", display: "inline-block", fontWeight: "bold", cursor: "pointer", marginLeft: "5px" }}>
                                            <span onClick={() => this.clearSelectedService()}> X</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {(hasNotificationCheckbox || hasTeleconsultationCheckbox) && (
                            <div className="row">
                                <div className="col-md-3 col-lg-3" />
                                {hasNotificationCheckbox && (
                                    <div className="col-md-3 col-lg-3">
                                        <CheckboxControl
                                            disabled={appEditModel.get("NotifyPatientDisabled") || isAppointmentEditDisabled}
                                            checked={appEditModel.get("NotifyPatient")}
                                            label={cacheStore.GetString("NotifyPatientLabel")}
                                            operation={LocalOperations.Calendar.Checkbox_Change}
                                            target={CheckboxTarget.Myle_Calendar_Appointment_NotifyPatient}
                                        />
                                    </div>
                                )}

                                {hasTeleconsultationCheckbox && (
                                    <div className="col-md-3 col-lg-3">
                                        <CheckboxControl
                                            checked={appEditModel.get("IsTeleconsultation")}
                                            label={cacheStore.GetString("TeleconsultationLabel")}
                                            onChange={() => this.onIsTeleconsultationClick()}
                                            disabled={isAppointmentEditDisabled}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {cacheStore.ClinicSettings.EnableCovidQuestionnaire &&
                            (!isEvent || appEditModel.get("Questionnaire")) &&
                            (
                                <div className="row">
                                    <div className="col-md-3 col-lg-3 vcenter text-right">
                                        <label className="form-label">{cacheStore.GetString("QuestionnaireLabel")}</label>
                                    </div>
                                    <div className="col-sm-9 col-md-9 col-lg-9 vcenter">
                                        {!appEditModel.get("Questionnaire") && (
                                            <TextboxControl
                                                value={appEditModel.get("Questionnaire")?.Name || ""}
                                                autoCompleteItems={availableQuestionnaires}
                                                onAutoCompleteSelectCallBack={(code, Model: QuestionnaireName) => this.handleQuestionnaireChange(Model)}
                                                disabled={isCombo || isAppointmentEditDisabled}
                                                dataCy="appointmentEditDialog-questionnaire"
                                            />
                                        )}
                                        {(appEditModel.get("Questionnaire") && !isAppointmentEditDisabled) && (
                                            <span style={{ verticalAlign: "sub" }} data-cy="appointmentEditDialog-questionnaire-span">
                                                {appEditModel.get("Questionnaire").renderLink(
                                                    this.handleOnQuestionnaireClicked,
                                                    appEditModel.get("StartTime"),
                                                    appEditModel.get("Appointment").AppointmentStateLookupId,
                                                    (appEditModel.get("OriginalPatientContactId") && appEditModel.get("OriginalPatientContactId") === appEditModel.get("SelectedPatientId"))
                                                        ? appEditModel.get("OriginalPatientContactId")
                                                        : null,
                                                    appEditModel.get("Appointment").IsSharedWithPortal
                                                )}
                                                {(!isCombo && appEditModel.get("Questionnaire").canBeRemoved(appEditModel.get("StartTime"))) && (
                                                    <span
                                                        style={{ "paddingLeft": "10px", color: "red", cursor: "pointer" }}
                                                        onClick={() => { sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Questionnaire_OnChange, { questionnaire: null }); }}
                                                    >X
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )
                        }

                        <div className="row">
                            <div className="col-md-3 col-lg-3 vcenter text-right">
                                <label className="form-label">
                                    {cacheStore.GetString("SitesLabel")}
                                </label>
                            </div>
                            <div id="selectedService" className="col-sm-9 col-md-9 col-lg-9">
                                <div id="appointment-edit-site">
                                    <select
                                        onChange={e => this.onSiteSelectedChange(e)}
                                        className="form-control myle-control"
                                        value={appEditModel.get("SelectedSiteId")}
                                        disabled={isCombo || isAppointmentEditDisabled}
                                        data-cy="appointmentEditDialog-sites-select"
                                    >
                                        {appointmentIntervenorSites.map(site => (
                                            <option
                                                key={site.Model.Id}
                                                value={site.Model.Id}
                                            >{site.Model.ClinicalName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-3 col-lg-3 vcenter text-right">
                                <label className="form-label">{cacheStore.GetString("LocationLabel")}</label>
                            </div>
                            <div className="col-sm-9 col-md-9 col-lg-9">
                                <TextboxControl
                                    value={appEditModel.get("RoomText")}
                                    autoCompleteItems={appEditModel.get("RoomsDisplayText").map(() => new SearchableItemModelT<string>("x")).toList()}
                                    onAutoCompleteSelectCallBackOperation={LocalOperations.Calendar.AppointmentEdit_Room_OnChange}
                                    operation={LocalOperations.Calendar.AppointmentEdit_Room_OnChange}
                                    disabled={isAppointmentEditDisabled}
                                    dataCy="appointmentEditDialog-room-textbox"
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-3 col-md-3 col-lg-3 vcenter text-right">
                                <label className="form-label">{cacheStore.GetString("DateLabel")}</label>
                            </div>
                            <div className="col-sm-9 col-md-9 col-lg-9">
                                <div className="flex-container">
                                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                        <div>
                                            <DateSelector
                                                width={110}
                                                dateFormat="yyyy-MM-dd"
                                                id="new_appt_date"
                                                dataCy="appointmentEditDialog-start-date"
                                                cssClass="rounded"
                                                date={appEditModel.get("StartTime")}
                                                onSelectOp={LocalOperations.Calendar.AppointmentEdit_StartDate_Set}
                                                disabled={isCombo || isAppointmentEditDisabled}
                                            />
                                        </div>
                                        <div style={{ marginLeft: "15px" }}>
                                            <TimeSelector
                                                width={80}
                                                height={33}
                                                interval={this.props.AppointmentTimePickerInterval}
                                                id="new_appt_starttime"
                                                dataCy="appointmentEditDialog-start-time"
                                                cssClass="rounded"
                                                date={appEditModel.get("StartTime")}
                                                onSelectOp={LocalOperations.Calendar.AppointmentEdit_StartTime_Set}
                                                disabled={isCombo || isAppointmentEditDisabled}
                                            />
                                        </div>

                                        <div style={{ marginLeft: "3px" }}>
                                            <label style={{ marginTop: "8px" }} className="form-label">{cacheStore.GetString("ToLabel")}</label>
                                        </div>
                                        <div style={{ marginLeft: "6px" }}>
                                            <TimeSelector
                                                width={80}
                                                height={33}
                                                interval={this.props.AppointmentTimePickerInterval}
                                                id="new_appt_endtime"
                                                dataCy="appointmentEditDialog-end-time"
                                                cssClass="rounded"
                                                date={appEditModel.get("EndTime")}
                                                onSelectOp={LocalOperations.Calendar.AppointmentEdit_EndTime_Set}
                                                disabled={isCombo || isAppointmentEditDisabled}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!isEvent && (
                            <div className="row flex-container" style={{ alignItems: "center", justifyContent: "flex-start" }}>
                                <div className="col-md-3 col-lg-3 vcenter text-right">
                                    <label className="form-label">{cacheStore.GetString("StateLabel")}</label>
                                </div>
                                <div className="col-sm-6 col-md-6 col-lg-6 inline-block" style={{ width: "336px" }}>
                                    <div className="flex-container">
                                        <div style={{ width: "306px" }}>
                                            <select
                                                id="__statusSelector"
                                                data-cy="appointmentEditDialog-stateCode-select"
                                                disabled={isAppointmentEditDisabled}
                                                onChange={e => this.onAppointmentStateChanged(e)}
                                                className="form-control myle-control"
                                                value={appEditModel.get("SelectedAppointmentStateCode")}
                                            >
                                                {appEditModel.get("AppointmentStates").map(state => (
                                                    <option key={state.LookupCode} value={state.LookupCode}>{cacheStore.GetLocalizedValueByLookupId(state.LookupID)}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <CheckboxControl
                                        checked={appEditModel.get("IsPaid")}
                                        label={cacheStore.GetString("PaidLabel")}
                                        onChange={() => this.onIsPaidClick()}
                                        disabled={isAppointmentEditDisabled}
                                    />
                                </div>
                            </div>
                        )}

                        {cacheStore.ClinicSettings.EnableCanadianTriageAndAcuityScore && (
                            <div className="row">
                                <div className="col-md-3 col-lg-3 vcenter text-right">
                                    <label className="form-label">{cacheStore.GetString("TriageLabel")}</label>
                                </div>
                                <div className="col-sm-6 col-md-6 col-lg-6 inline-block">
                                    <div className="flex-container" style={{ width: "68px" }}>
                                        <select
                                            className="form-control myle-control"
                                            value={appEditModel.get("SelectedCanadianTriageAndAcuityScore") || ""}
                                            onChange={e => this.onTriageScoreChanged(e)}
                                        >
                                            <option value="" />
                                            {cacheStore.CanadianTriageAndAcuityScoreList.map(score =>
                                                <option key={score} value={score}>{score}</option>
                                            )}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="row">
                            <div className="col-md-3 col-lg-3 vcenter text-right">
                                <label className="form-label">{cacheStore.GetString("StaffLabel")}</label>
                            </div>
                            <div className="col-sm-9 col-md-9 col-lg-9">
                                <div style={{ marginTop: "2px" }}>
                                    <PersonSearchAutocompleteCustom
                                        componentId="appointment_edit_clinicians"
                                        dataCy="appointmentEditDialog-clinicians"
                                        selectedItemList={appEditModel.get("SelectedIntervenors")}
                                        selectionOp={LocalOperations.Calendar.AppointmentEdit_Intervenors_OnChange}
                                        clearOp={LocalOperations.Calendar.AppointmentEdit_Intervenors_OnChange}
                                        focusOnLoad={false}
                                        disabled={isAppointmentEditDisabled}
                                        closeDropDownAfterSelection={true}
                                        searchStrategy={this.clinicianSearchStrategy}
                                        allowMultipleItemSelection={true}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-3 col-lg-3 text-right">
                                <label className="form-label">{cacheStore.GetString("NotesDLabel")}</label>
                            </div>
                            <div className="col-sm-9 col-md-9 col-lg-9">
                                <TextAreaResizable
                                    id="task_body_box"
                                    dataCy="appointmentEditDialog-notes-textbox"
                                    minHeightPx={70}
                                    text={appEditModel.get("Notes")}
                                    isDisabled={isAppointmentEditDisabled}
                                    onChange={value => this.onNoteChanged(value)}
                                    setCursorStartOnFocus={true}
                                    resetTextOnIdChange={true}
                                    verticalResizeOnly={true}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-3 col-lg-3 text-right" />
                            <div className="col-sm-9 col-md-9 col-lg-9">
                                <textarea rows={7} value={appEditModel.get("PreviousNotes")} className="form-control" onChange={null} disabled />
                            </div>
                        </div>

                        {(!isEvent || appEditModel.get("ReferralDoctors").size > 0 || appEditModel.get("ReferralDoctorSearchString")) && (
                            <div className="row">
                                <div className="col-md-3 col-lg-3 vcenter text-right">
                                    <label className="form-label">{cacheStore.GetString("ReferralLabel")}</label>
                                </div>
                                <div className="col-sm-9 col-md-9 col-lg-9">
                                    <FeatureFlagControl
                                        flag={cacheStore.ClinicSettings.EnableContactDirectory}
                                        activeFeature={(
                                            <DirectoryProfessionalSearchLockingComponent
                                                componentId="appointmentEditDialog-referralDoctorAdd"
                                                disabled={isAppointmentEditDisabled}
                                                onItemSelected={item => {
                                                    const provider = DirectoryHealthCareProvider.toPerson(item.entity as DirectoryHealthCareProvider);
                                                    sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Referrals_OnChange, {
                                                        value: new PersonDropdownItem({ Item: provider }),
                                                        shouldSaveAsCustomReferral: true // needed so that the referral is not later retrieved from the LicencedIntervenor table in LoadAppointmentsRequestHandler
                                                    });
                                                }}
                                                selectedItems={appEditModel
                                                    .get("ReferralDoctors")
                                                    .map(p => ({ Name: `${p.Name}${p.Licence ? ` (${p.Licence})` : ""}` }))
                                                    .toArray()
                                                }
                                                onTextChange={text => sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Referrals_OnTextChange, { value: text })}
                                                searchText={appEditModel.get("ReferralDoctorSearchString")}
                                                dataCy="contactDirectory-appointmentEditDialog-referralDoctorSearch"
                                                onRemoveItem={() => sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Referrals_OnClear)}
                                            />
                                        )}
                                        fallbackFeature={(
                                            <ReferalDoctorSearchAutocompleteCustom
                                                useblurevent={true}
                                                focusOnLoad={false}
                                                displayList={true}
                                                disabled={isAppointmentEditDisabled}
                                                componentId="add_appt_referal"
                                                selectedDoctors={appEditModel.get("ReferralDoctors")}
                                                selectionOp={LocalOperations.Calendar.AppointmentEdit_Referrals_OnChange}
                                                clearOp={LocalOperations.Calendar.AppointmentEdit_Referrals_OnClear}
                                                closeDropDownAfterSelection={true}
                                                changedTextValueEvent={LocalOperations.Calendar.AppointmentEdit_Referrals_OnTextChange}
                                                defaultTextValue={appEditModel.get("ReferralDoctorSearchString")}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        )}

                        {(!isEvent || appEditModel.get("ReferralPhoneNumber")) && (
                            <div className="row">
                                <div className="col-md-3 col-lg-3 vcenter text-right">
                                    <label className="form-label">{cacheStore.GetString("PhoneLabel")}</label>
                                </div>
                                <div className="col-sm-9 col-md-9 col-lg-9">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={appEditModel.get("ReferralPhoneNumber")}
                                        onChange={e => this.onReferralPhoneChanged(e)}
                                        disabled={isAppointmentEditDisabled}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="row">
                            <div className="col-md-3 col-lg-3 text-right">
                                <label className="form-label bold">{cacheStore.GetString("RecurrenceLabel")}</label>
                            </div>
                            <div className="col-sm-9 col-md-9 col-lg-9" />
                        </div>
                        {this.props.AppointmentEditModel.get("IsNewAppointment") && (
                            <>
                                <div className="row">
                                    <div className="col-md-3 col-lg-3 vcenter text-right">
                                        <label className="form-label">{cacheStore.GetString("TypeLabel")}</label>
                                    </div>
                                    <div className="col-sm-9 col-md-9 col-lg-9">
                                        <select
                                            onChange={this.onRecurrenceChange.bind(this)}
                                            className="form-control"
                                            data-cy="appointmentEditDialog-recurrenceType-select"
                                            value={this.props.AppointmentEditModel.get("SelectedRecurrenceType").toString()}
                                            disabled={isAppointmentEditDisabled}
                                        >
                                            {this.props.AppointmentEditModel.get("RecurrenceTypes").toArray().map(type => (
                                                <option key={type.RecurrenceType.toString()} value={type.RecurrenceType.toString()}>{type.Description}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-sm-3 col-md-3 col-lg-3 vcenter text-right">
                                        <label className="form-label">{cacheStore.GetString("EndDateDLabel")}</label>
                                    </div>
                                    <div className="col-sm-4 col-md-4 col-lg-4">
                                        <DateSelector
                                            id="recurrence_end_date"
                                            dataCy="appointmentEditDialog-recurrenceEndDate-selector"
                                            cssClass="rounded"
                                            date={this.props.AppointmentEditModel.get("RecurrenceEndDate")}
                                            onSelectOp={LocalOperations.Calendar.AppointmentEdit_Recurrence_EndDate_Set}
                                            dateFormat="yyyy/MM/dd"
                                            disabled={isAppointmentEditDisabled}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        {!this.props.AppointmentEditModel.get("IsNewAppointment") && (
                            <div className="row">
                                <div className="col-sm-3 col-md-3 col-lg-3" />
                                <div className="col-sm-9 col-md-9 col-lg-9">
                                    <span>{cacheStore.GetString("AppointmentInvalidRecurrenceLabel")}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private renderHeaderCreatePatient(): JSX.Element {
        return (
            <div className={styles.header}>
                <h4 className={styles.title}>{cacheStore.GetString("AppointmentDetailsLabel")}: {cacheStore.GetString("CreatePatientLabel")}</h4>
                <a className={styles.close} onClick={() => this.onCancelClick()}>x</a>
            </div>
        );
    }

    private renderFooterCreatePatient(): JSX.Element {
        return (
            <div className={styles.footer}>
                <div className="row clear-margin">
                    <div className="col-lg-5" />
                    <div className="col-lg-7 text-right">
                        <ButtonControl
                            className="btn btn-default push-right-5"
                            onClick={() => this.onCancelPatientCreation()}
                            label={cacheStore.GetString("CancelLabel")}
                        />
                        <ButtonControl
                            className="btn btn-primary push-right-5"
                            disabled={!this.state.PatientCreateFormIsValid || this.state.isPatientSaving || this.state.PatientCreateEditState.get("DuplicatePopupOpen")}
                            onClick={() => this.onSavePatientCreation()}
                            label={cacheStore.GetString("CreateLabel")}
                            dataCy="appointmentEditDialog-createPatient-button"
                        />
                    </div>
                </div>
            </div>
        );
    }

    private renderCreatePatientForm(): JSX.Element {
        return (
            <div className="scroll-limiter-main">

                {this.props.AppointmentEditModel.get("IsLoadingPatientProfileFromPortal")
                    ? (
                        <div>
                            {cacheStore.GetString("LoadingPortalPatientInfoLabel")}  <img src={img_loading} />  <a onClick={() => this.onCancelFetchPatientInfoFromPortal()}> {cacheStore.GetString("CancelAction")} </a>
                        </div>
                    )
                    : (
                        <PatientEdit
                            id="calpe"
                            patientModel={this.props.PatientCreationModel}
                            stateUpdateFunction={state => this.notifyCreatePatientUpdate(state)}
                            hideFooter={true}
                            disabled={this.props.AppointmentEditModel.get("IsLoadingPatientProfileFromPortal")}
                            duplicatePromptLabelName="DuplicateSentencePromptCalendarLabel"
                            duplicateContinueOperation={LocalOperations.Calendar.AppointmentEdit_Duplicate_AssociatePatient}
                            cssClass="new-patient-popup"
                            duplicatePopupCSS="adjust-margin"
                            enableQuickPatientConversionForDuplicates
                        />
                    )
                }

            </div>
        );
    }

    private renderLinkPatientPortalForm(): JSX.Element {
        const dateOfBirth = moment(this.props.PatientPortalModel.PortalPatient?.DateOfBirth);
        const dateOfBirthFormatted = dateOfBirth.isValid() ? dateOfBirth.utc().format("YYYYMMDD") : "";

        return (
            <div className="scroll-limiter-main">

                {this.props.AppointmentEditModel.get("IsLoadingPatientProfileFromPortal")
                    ? (
                        <div>
                            {cacheStore.GetString("LoadingPortalPatientInfoLabel")}
                            <img src={img_loading} />
                            <a onClick={() => this.onCancelFetchPatientInfoFromPortal()}>
                                {cacheStore.GetString("CancelAction")}
                            </a>
                        </div>
                    )
                    : (
                        <PortalPatientSync
                            componentId="portalSyncPopup"
                            person={this.props.PatientPortalModel}
                            onCancelCallback={() => this.onCancelLinkPatientPortal()}
                            onPatientCreatedCallback={() => this.onPatientPortalCreatedOrAttached()}
                            onPatientAttachedCallback={() => this.onPatientPortalCreatedOrAttached()}
                            initialSearchValue={this.props.PatientPortalModel.NAM || (this.props.PatientPortalModel.Name + " " + dateOfBirthFormatted).trim()}
                        />
                    )
                }
            </div>
        );
    }

    private renderPatientInformationPanel(): JSX.Element {
        return (
            <div style={{ width: "475px", paddingRight: "23px" }}>
                <div className="row clear-margin">
                    <div className="col-sm-12 col-md-12 col-lg-12 patient-view-side">
                        <div className="scroll-limiter-main">
                            {this.props.PatientBannerModel != null && (
                                <PatientBannerVertical
                                    model={this.props.PatientBannerModel}
                                    recallCompleteOperation={LocalOperations.Calendar.AppointmentEdit_OnCompleteRecall}
                                    appointmentEditModel={this.props.AppointmentEditModel}
                                    activeSite={cacheStore.GetSiteById(this.props.AppointmentEditModel.get("SelectedSiteId"))}
                                    hasEmailNotificationsEnabled={this.state.hasEmailNotificationsEnabled || this.props.AppointmentEditModel.get("IsTeleconsultation")}
                                    hasSmsNotificationsEnabled={this.state.hasSmsNotificationsEnabled}
                                    showPopulationBasedStatusIcon
                                    addPortalCommunicationSection
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderLinkThirdPartyPatientForm(): JSX.Element {
        return (
            <div className="scroll-limiter-main">
                {this.props.AppointmentEditModel.get("IsLoadingThirdPartyPatientData")
                    ? (
                        <div>
                            {cacheStore.GetString("LoadingPortalPatientInfoLabel")}
                            <img src={img_loading} />
                            <a onClick={() => this.onCancelFetchThirdPartyPatientInfo()}>
                                {cacheStore.GetString("CancelAction")}
                            </a>
                        </div>
                    )
                    : (
                        <PatientImportComponent
                            componentId="PatientImportComponent"
                            handler={new ThirdPartyPatientImportHandler(this.state.importedPatients)}
                            patient={this.props.AppointmentEditModel.get("ThirdPartyPatient")}
                            linkLabel="LinkLabel"
                            createLabel="CreateLabel"
                            enablePatientCreation={cacheStore.ClinicSettings.EnablePatientEdit}
                            onClose={() => sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_LinkThirdPartyPatientCancel)}
                            onErrorOkClick={() => sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_LinkThirdPartyPatientCancel)}
                            onLink={existingPatientContactFacetId => this.onCreateNewPatientToLinkExternalPatient(existingPatientContactFacetId)}
                            onCreate={patientCreationModel => this.onCreateNewPatientToLinkExternalPatient(null, patientCreationModel)}
                            patientValidationRules={this.externalPatientImportValidationRules}
                            duplicateCheckOnLinkEnabled={false}
                        />
                    )
                }
            </div>
        );
    }

    private onEventTabClick(): void {
        const patientSearchString = this.props.AppointmentEditModel.get("PatientSearchString");
        const selectedPatients = this.props.AppointmentEditModel.get("SelectedPatient");

        if (!patientSearchString?.trim() && selectedPatients.size === 0) {
            sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Tab_OnChange, { isCalendarEventTab: true });
        }
    }

    private async onCreateNewPatientToLinkExternalPatient(existingPatientContactFacetId: string = null, patient: IPatientCreationModel = null): Promise<void> {
        const createPatientRequest = patient && {
            SelectedPrefixLookupId: patient.get("Prefix"),
            FirstName: patient.get("FirstName"),
            PreferredName: patient.get("PreferredName"),
            LastName: patient.get("LastName"),
            GenderLookupId: patient.get("GenderLookupId"),
            DateOfBirth: patient.get("DOB"),
            CivicAddress: patient.get("CivicAddressEdit"),
            City: patient.get("CityEdit"),
            PostalCode: patient.get("PostalCodeEdit"),
            NAM: patient.get("NAMEdit"),
            NAMExpiry: patient.get("NAMExpiryEdit"),
            CountryLookupId: patient.get("SelectedCountryLookupId"),
            RegionLookupId: patient.get("SelectedRegionLookupId"),
            Phone1Number: patient.get("Phone1Edit"),
            Phone1ContactTypeLookupId: patient.get("Phone1ContactTypeLookupId"),
            Phone1TypeLookupId: patient.get("Phone1TypeLookupId"),
            Phone1Ext: patient.get("Phone1Extension"),
            Phone1NotificationsEnabled: patient.get("Phone1NotificationsEnabled"),
            Phone2Number: patient.get("Phone2Edit"),
            Phone2ContactTypeLookupId: patient.get("Phone2ContactTypeLookupId"),
            Phone2TypeLookupId: patient.get("Phone2TypeLookupId"),
            Phone2Ext: patient.get("Phone2Extension"),
            Phone2NotificationsEnabled: patient.get("Phone2NotificationsEnabled"),
            EmailAddress: patient.get("EmailEdit"),
            EmailTypeByLookup: patient.get("EmailTypeByLookup"),
            EmailNotificationsEnabled: patient.get("EmailNotificationsEnabled"),
            IsOkToContact: patient.get("IsOkToContact"),
            PreferredSpokenLanguageLookupId: patient.get("PreferredSpokenLanguageLookupId"),
            MotherLastName: patient.get("motherLastName"),
            MotherFirstName: patient.get("motherFirstName"),
            HealthCardRegionLookupId: IJPatientCreationModel.getHealthCardRegionLookupId(patient)
        };

        const response = await Client.postAsync(
            ServerOperations.Server.Myle_Calendar_LinkAppointmentThirdPartyPatient,
            {
                PatientContactFacetId: existingPatientContactFacetId,
                ThirdPartyPatientId: this.props.AppointmentEditModel.get("ThirdPartyPatient").Id,
                PatientCreationRequest: createPatientRequest
            }
        );

        if (!response.isSuccess)
            sessionStore.showPopup(response.errors);
        else
            sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_LinkThirdPartyPatientSuccess);
    }

    private stopPropagation(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    private clearSelectedService(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Subject_onClearSelectedService);
    }

    private onSiteSelectedChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Site_OnChange, { value: e.target.value });
    }

    private onAppointmentStateChanged(e: React.ChangeEvent<HTMLSelectElement>): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_State_OnChange, { value: e.target.value });
    }

    private onTriageScoreChanged(e: React.ChangeEvent<HTMLSelectElement>): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_CanadianTriageAndAcuityScore_OnChange, { value: e.target.value });
    }

    private onReferralPhoneChanged(e: React.ChangeEvent<HTMLInputElement>): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_ReferralPhone_OnChange, { value: e.target.value });
    }

    private onRecurrenceChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Recurrence_OnChange, { value: e.target.value });
    }

    private onNoteChanged(value: string): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Notes_OnChange, { value });
    }

    private onCancelClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_OnCancel);
    }

    private onSaveClick(): void {
        this.mutateState(
            s => s.IsSaving = true,
            () => this.checkContactPointsAndSave(false)
        );
    }

    private onSaveAndOpenClick(): void {
        this.mutateState(
            s => s.IsSaving = true,
            () => this.checkContactPointsAndSave(true)
        );
    }

    private async checkContactPointsAndSave(isSaveAndOpenAction: boolean): Promise<void> {
        try {
            const patientContactId = this.props.AppointmentEditModel.get("SelectedPatientId");

            if (patientContactId && this.isTeleconsultation()) {
                await this.ensurePatientHasValidEmail();
            }
            else if (this.hasQuestionnaire
                && patientContactId
                && this.isPatientAnAdult()
                && this.hasNoValidContactPoints()) {
                this.mutateState(
                    s => {
                        s.IsSaving = true;
                        s.isSaveAndOpenAction = isSaveAndOpenAction;
                        s.showAddContactPointWarning = true;
                    }
                );
                return;
            }
            else if (this.hasQuestionnaire
                && patientContactId
                && !this.isPatientAnAdult()) {
                const patientRepresentantValidator = await PatientHelper.getPatientEmailValidator(patientContactId);

                if (!patientRepresentantValidator.PatientHasRepresentantWithValidContact)
                    await this.openAddEmailPopup(patientRepresentantValidator, this.hasQuestionnaire);
            }

            isSaveAndOpenAction ? this.saveAndOpen() : this.save();
        }
        catch (e) {
            console.error(e);
            this.mutateState(s => s.IsSaving = false);
        }
    }

    private isPatientAnAdult(): boolean {
        return (
            PatientHelper.getPatientIsOfAgeOfConsent(this.props.PatientBannerModel.get("Model")?.IndividualResponse?.DateOfBirth)
            || !this.props.PatientBannerModel.get("Model")?.IndividualResponse?.DateOfBirth
        );
    }

    private hasNoValidContactPoints(): boolean {
        const patient = this.props.PatientBannerModel.get("Model");

        return (isArrayNullOrUndefinedOrEmpty(patient?.Emails)
            && (!cacheStore.portalConfiguration?.HasSmsNotificationEnabled || isArrayNullOrUndefinedOrEmpty(patient?.Phones)));
    }

    private async ensurePatientHasValidEmail(): Promise<void> {
        await PatientHelper.ensurePatientHasValidEmail(
            this.props.AppointmentEditModel.get("SelectedPatientId"),
            async patientEmailValidator => this.openAddEmailPopup(patientEmailValidator)
        );
    }

    private async openAddEmailPopup(patientEmailValidator: PatientEmailValidator, hasQuestionnaire: boolean = false): Promise<void> {
        return new Promise((resolve, reject) => {
            this.mutateState(s => {
                s.addEmailPopupProps = this.initAddEmailPopupControlProps(
                    () => resolve(),
                    () => reject("Adding new email cancelled."),
                    patientEmailValidator,
                    hasQuestionnaire
                );
                s.showAddEmailPopup = true;
            });
        });
    }

    private initAddEmailPopupControlProps(
        onSubmit: () => void,
        onCancel: () => void,
        patientEmailValidator: PatientEmailValidator,
        hasQuestionnaire: boolean
    ): AddRepresentativeEmailPopupProps {
        const patientUnderAgeWithoutRepresentantLabel: string = hasQuestionnaire
            ? "QuestionnaireRequiresRepresentativeLabel"
            : "TeleconsultationRequresEmailAndPatientUnderAgeOfConsentWithoutRepresentativeLabel";

        const patientWithoutRepresentantContactLabel: string = hasQuestionnaire
            ? "QuestionnaireRequiresRepresentativeContactLabel"
            : "TeleconsultationRequresEmailAndPatientUnderAgeOfConsentLabel";

        return {
            ...AddRepresentativeEmailPopupProps.defaultProps,
            representantsDto: patientEmailValidator.Representants,

            enablePhoneContent: hasQuestionnaire,

            patientUnderAgeWithoutRepresentantLabel: patientUnderAgeWithoutRepresentantLabel,

            patientWithoutRepresentantContactLabel: patientWithoutRepresentantContactLabel,

            onCancel: () => {
                this.mutateState(
                    s => {
                        s.showAddEmailPopup = false;
                        s.IsSaving = false;
                    },
                    () => onCancel()
                );
            },

            onSubmit: (ids: string[]) => {
                this.addCreatedContactPointIdsToStore(ids);

                this.mutateState(
                    s => s.showAddEmailPopup = false,
                    () => onSubmit()
                );
            },

            onContinueWithoutContact: () => {
                this.mutateState(
                    s => s.showAddEmailPopup = false,
                    () => onSubmit()
                );
            }
        };
    }

    private addCreatedContactPointIdsToStore(ids: string[]): void {
        calendarStore.appointmentEditModel = calendarStore.appointmentEditModel
            .set(
                "ContactPointNotificationList",
                calendarStore.appointmentEditModel
                    .get("ContactPointNotificationList")
                    .push(...ids)
            );
    }

    private save(): void {
        this.setStateAsSavingState();
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_OnSave);
    }

    private saveAndOpen(): void {
        this.setStateAsSavingState();
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_OnSaveAndOpen);
    }

    private setStateAsSavingState(): void {
        this.mutateState(s => s.IsSaving = true);
    }

    private onCopyClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Copy);
    }

    private onCutClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Cut);
    }

    private onPasteClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Paste);
    }

    private onPrintClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_PrintLabel);
    }

    private onIsPaidClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_IsPaidCheck);
    }

    private onIsTeleconsultationClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_IsTeleconsultationCheck);
    }

    private notifyCreatePatientUpdate(patientEditState: Readonly<PatientEditState>): void {
        const s = new EditAppointmentDialogState(this.state);
        s.PatientCreateEditState = patientEditState.patientModel;
        s.PatientCreateFormIsValid = PatientEditState.IsValid(patientEditState);
        this.setState(s);
    }

    private onCreatePatientClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_NewPatientClick);
    }

    private onCancelPatientCreation(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_NewPatient_Cancel);
    }

    private onSavePatientCreation(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_NewPatient_Save, {
            patientModel: this.state.PatientCreateEditState
        });

        const s = new EditAppointmentDialogState(this.state);
        s.isPatientSaving = true;
        this.setState(s);

        window.setTimeout(() => {
            const s = new EditAppointmentDialogState(this.state);
            s.isPatientSaving = false;
            this.setState(s);
        }, 1000);
    }

    private onCancelFetchPatientInfoFromPortal(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_CancelPortalPatientFetch);
    }

    private onCancelFetchThirdPartyPatientInfo(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_LinkThirdPartyPatientCancel);
    }

    private onLinkPatientPortalClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_LinkPatientPortalClick, {
            isFromPortal: true,
            patientProfileId: this.props.AppointmentEditModel.get("Appointment").ExternalId
        });
    }

    private onLinkExternalPatientClick(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_LinkThirdPartyPatientClick, {
            patientSnapshotId: this.props.AppointmentEditModel.get("Appointment").PatientSnapshotId
        });
    }

    private onCancelLinkPatientPortal(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_LinkPatientPortal_Cancel);
    }

    private onPatientPortalCreatedOrAttached(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_LinkPatientPortal_CreatedOrAttached);
    }

    private isCombo(): boolean {
        return this.props.AppointmentEditModel.get("Appointment") != null && !isNaN(this.props.AppointmentEditModel.get("Appointment").ComboId);
    }

    private blockFieldsForPortalAppointment(): void {
        if (this.isCombo()) {
            // disable input fields that cannot be modified
            // $("#add_appointment_patients").block({ message: null });
            // $(".patient-autocomplete").block({ message: null });
            // $(".k-widget.k-multiselect.k-header").block({ message: null });
            $("#appointment-edit-site").block({ message: null });
            $("#selectedService").block({ message: null });
        }
    }

    private initData(): void {
        this.clinicianSearchStrategy = new ClinicianSearchStrategy(this.props.AppointmentEditModel.get("Intervenors").toArray());
        this.externalPatientImportValidationRules = [
            (importedPatientInfo: IImportablePatient, selectedPatient: Person) => (importedPatientInfo?.NAM && selectedPatient?.NAM && importedPatientInfo?.NAM != selectedPatient?.NAM)
                ? new PatientImportValidationResult(false, "LinkPatientNamValidationFailLabel")
                : new PatientImportValidationResult(true, null)
        ];
    }

    private handleQuestionnaireChange(questionnaire: QuestionnaireName): void {
        const questionnaireStatusInfo = new QuestionnaireStatusInfo({
            QuestionnaireInstanceId: GuidHelper.Empty(),
            AppointmentId: this.props.AppointmentEditModel.get("Appointment")?.AppointmentId,
            QuestionnaireId: questionnaire.QuestionnaireId
        });

        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Questionnaire_OnChange, {
            questionnaire: questionnaireStatusInfo
        });
    }

    private handleOnQuestionnaireClicked(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_Questionnaire_OnClick, {
            showQuestionnaire: true
        });
    }

    private get hasQuestionnaire(): boolean {
        return cacheStore.ClinicSettings.EnableCovidQuestionnaire && !!this.props.AppointmentEditModel.get("Questionnaire");
    }

    private isTeleconsultation(): boolean {
        return cacheStore.ClinicSettings.EnableTelemedFeature
            && cacheStore.ClinicSettings.EnableIsTeleconsultationCheckbox
            && this.props.AppointmentEditModel.get("IsTeleconsultation");
    }

    private onPrintCertificate(): void {
        sessionStore.dispatch(LocalOperations.Calendar.AppointmentEdit_PrintCertificate);
    }

    private shouldShowPrintCertificate(isPortalAppointment: boolean): boolean {
        return this.hasAvailablePatient(isPortalAppointment) && !this.isCancelled();
    }

    private hasAvailablePatient(isPortalAppointment: boolean): boolean {
        return this.props.AppointmentEditModel.get("SelectedPatient").size > 0
            || isPortalAppointment
            || !IsNullOrWhiteSpace(this.props.AppointmentEditModel.get("PatientSearchString"));
    }

    private isCancelled(): boolean {
        const cancelledStateCodes = ["NOSHOW", "CANCELED", "CANCELLED BY PATIENT"];

        return cancelledStateCodes.any(code => code === this.props.AppointmentEditModel.get("SelectedAppointmentStateCode"));
    }
}

export { EditAppointmentDialog };
